<template>
  <info-panel
    v-if="index"
    :item="index"
    :title="index?.title"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>
      {{ $t("labels.showEmptySections") }}
    </template>
    <template #label-valueInHeader>
      {{ $t("labels.valueInHeader") }}
    </template>

    <template #header v-if="settingsValues.valueInHeader && sceneNode && (sceneNode.data.valueVisible || sceneNode.data.barVisible)">
      <div>
        <h2 class="info-panel__type">{{ $t("words.currentValue") }}</h2>
        <net-item-value-bar
          v-if="sceneNode.data.barVisible && maxValue != null && minValue != null"
          :value="Number(calculatedValue?.value)"
          :unit="calculatedValue?.unit || startValue?.unit || ''"
          :min-value="minValue"
          :max-value="maxValue"
          :value-visible="sceneNode.data.valueVisible"
          class="target-infopanel__value"
        />
        <net-item-value-plain
          v-else-if="sceneNode.data.valueVisible"
          :value="calculatedValue?.value"
          :unit="calculatedValue?.unit ?? ''"
          class="target-infopanel__value"
        />
      </div>
    </template>

    <template #label-image>
      {{ $t("words.image") }}
    </template>
    <template
      #image
      v-if="
        imageData &&
        (extraSettings.image?.value == 'side-full' ||
          extraSettings.image?.value == 'side-cut')
      "
    >
      <img
        :src="imageData"
        :class="{
          'info-panel__image-full': extraSettings.image?.value == 'side-full',
          'info-panel__image-cut': extraSettings.image?.value == 'side-cut'
        }"
      />
    </template>
    <template
      #header-image
      v-if="imageData && extraSettings.image?.value == 'icon'"
    >
      <img :src="imageData" />
    </template>
    <template #option-image-icon>{{ $t("labels.imageAsIcon") }}</template>
    <template #option-image-hidden>{{ $t("labels.hidden") }}</template>
    <template #option-image-side-full>{{
      $t("labels.imageAtSideFull")
    }}</template>
    <template #option-image-side-cut>{{
      $t("labels.imageAtSideCut")
    }}</template>

    <template #label-description>
      {{ $t("labels.description") }}
    </template>
    <template
      #description
      v-if="settingsValues.showEmptySections || !isEmpty(index.description)"
    >
      <formatted-read-more-text
        :value="index.description"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <template #label-value>
      {{ $t("labels.value") }}
    </template>
    <template #label-_formula>{{ $t("labels.formula") }}</template>
    <template #value>
      <div class="info-panel__table">
        <template v-if="sections._formula">
          <h4 class="label mt-0">{{ $t("labels.formula") }}</h4>
          <expression-rendered
            :expression="index.valueExpression"
            :parse-context="parseContext"
            class="info-panel__expression"
          />
        </template>

        <span class="label">{{ $t("words.currentValue") }}</span>
        <span>{{ formatNumber(calculatedValue?.value) }} {{ calculatedValue?.unit }}</span>

        <span class="label">{{ $t("words.startValue") }}</span>
        <span>{{ formatNumber(startValue?.value) }} {{ startValue?.unit }}</span>

        <span class="label">{{ $t("labels.valueRange") }}</span>
        <span v-if="minValue == null && maxValue == null">—</span>
        <span v-else>{{ formatNumber(minValue) }} – {{ formatNumber(maxValue) }}</span>
      </div>
    </template>

    <template #label-formulatree>{{ $t("labels.formulaTree") }}</template>
    <template #formulatree>
      <expression-tree-view
        class="expression-editor__tree-view"
        :expression="index.valueExpression"
        :parse-context="parseContext"
        :editable="false"
      />
    </template>

    <template #label-timechart>
      {{ $t("labels.stateOverTime") }}
    </template>
    <template #timechart="{ width }">
      <Chart
        v-if="isSimulationOpen()"
        :width="width"
        :height="300"
        :chart-item="chartOptions"
      />
      <p v-else class="message mv-0 message-info">
        {{ $t("messages.runSimulationForChart") }}
      </p>
    </template>

    <template #label-impacts>
      {{ $t("objectNames.impact", 5) }}
    </template>
    <template #heading-impacts>
      {{ $t("labels.outgoingRelations") }}
    </template>
    <template
      #impacts
      v-if="settingsValues.showEmptySections || outgoingRelations.length > 0"
    >
      <button-list
        :items="outgoingRelations"
        :transition-active="false"
        @select="openOverlay($event)"
      >
        <template #default="{ item: relation }: { item: RelationItem }">
          <span v-if="items[relation.endItemType][relation.endItemId]?.title">
            {{ items[relation.endItemType][relation.endItemId].title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </template>
        <template #placeholder>
          <p class="message mv-0">
            {{ $t("errors.noOutgoingRelations") }}
          </p>
        </template>
      </button-list>
    </template>

    <template #label-stakeholders>
      {{ $t("objectNames.stakeholder", 2) }}
    </template>
    <template
      #stakeholders
      v-if="settingsValues.showEmptySections || stakeholders.length > 0"
    >
      <button-list
        :items="stakeholders"
        prop="title"
        :transition-active="false"
        @select="openOverlay($event)"
      >
        <template #default="{ item: stakeholder }">
          <span v-if="stakeholder.title">{{ stakeholder.title }}</span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </template>
        <template #placeholder>
          <p class="message mv-0">
            {{ $t("errors.noStakeholdersAssigned") }}
          </p>
        </template>
      </button-list>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { mapValues } from "@/helpers/utility/CollectionFunctions";
import { formatNumber } from "@/helpers/utility/Formatting";
import { isEmpty } from "@/helpers/utility/Text";
import { useRoute } from "vue-router";

import ButtonList from "@core/ui/ButtonList.vue";
import InfoPanel from "@editor/ui/InfoPanel.vue";
import ExpressionRendered from "../expression/ExpressionRendered.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import NetItemValueBar from "@editor/net/NetItemValueBar.vue";
import NetItemValuePlain from "@editor/net/NetItemValuePlain.vue";
import ExpressionTreeView from "../expression/ExpressionTreeView.vue";
import Chart from "@editor/items/charts/Chart.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import { useStakeholderStore } from "@editor/items/stakeholder/stakeholders.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useSceneTree } from "../scene/sceneTree";
import { useElements } from "../element/elements";
import { useRandomValuesStore } from "../expression/expressionRandomValues.ts";

import type { IndexItem } from "./IndexItem";
import type { RelationItem } from "@editor/items/relation/RelationItem";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { ChartItemOptions } from "../charts/useChartData";
import type { ExpressionParseContext } from "../effect/Expression.ts";

const props = defineProps<{
  index: IndexItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const route = useRoute();
const imageStore = useImageStore();
const indexStore = useIndexStore();
const simulationViewStore = useSimulationViewStore();

const sections = reactive({
  description: true,
  timechart: true,
  value: true,
  _formula: true,
  formulatree: true,
  impacts: true,
  stakeholders: true
});
const extraSettings = reactive({
  image: {
    type: "dropdown" as const,
    value: "icon",
    options: ["icon", "side-full", "side-cut", "hidden"]
  },
  showEmptySections: { type: "check" as const, value: true },
  valueInHeader: { type: "check" as const, value: false }
});

const infoPanelStore = useInfoPanelStore();


// =====================
// VALUE

const targets = computed(() => useTargetStore().items);
const resources = computed(() => useResourceStore().items);
const indices = computed(() => indexStore.items);
const items = computed(() => {
  return {
    target: targets.value,
    resource: resources.value,
    index: indices.value
  };
});

const startValue = computed(() => {
  if (props.index == null) return null;
  return indexStore.expressionValues[props.index.uid] ?? null;
});

const calculatedValue = computed(() => {
  if (props.index == null) return null;
  if (!inSimulationView.value) return indexStore.expressionValues[props.index.uid] ?? null;
  return simulationViewStore.elementValues[props.index.uid];
});

const minValue = computed(() => {
  return calculatedValue.value?.minValue ?? startValue.value?.minValue ?? null;
});
const maxValue = computed(() => {
  return calculatedValue.value?.maxValue ?? startValue.value?.maxValue ?? null;
});

const inSimulationView = computed(() => {
  return ["simulation", "inspector", "evaluation"].includes(route?.meta?.areaName as string);
});

function isSimulationOpen() {
  if (!["simulation", "inspector"].includes(route.meta.areaName as string))
    return false;
  if (route.meta.areaName === "inspector") {
    if (useInspectorView().subview != "matrix") return false;
  }
  return simulationViewStore.simulation != null;
}

// ====================
// NODE

const sceneTree = useSceneTree();

const treeNode = computed(() => {
  if (props.index == null) return null;
  return sceneTree.elementNodes.get(props.index.uid) || null;
});
const sceneNode = computed(() => treeNode.value?.node || null);

// =====================
// CHART

const randomValuesStore = useRandomValuesStore();

const parseContext = computed((): ExpressionParseContext => {
  if (!isSimulationOpen()) return {
    rounds: [{
      elementValues: useElements().elementValues,
      index: 0
    }],
    affectedType: 'index',
    affectedId: props.index?.id ?? -1,
    randomKey: props.index?.uid ?? '',
    randomValues: randomValuesStore.randomValues,
    roundIndex: 0
  };
  return {
    rounds: [{
      elementValues: simulationViewStore.elementValues,
      index: simulationViewStore.selectedRoundIndex
    }],
    affectedType: 'index',
    affectedId: props.index?.id ?? -1,
    randomKey: props.index?.uid ?? '',
    randomValues: simulationViewStore.selectedRound?.startRandomValues || {},
    roundIndex: simulationViewStore.selectedRoundIndex
  };
});

const maxRoundRange = computed(() => {
  return Math.max(useSettingsStore().simulationRoundAmount - 1, (simulationViewStore.simulation?.maxRounds ?? 0) - 1, 0);
});

const chartOptions = computed((): ChartItemOptions => ({
  uid: props.index?.uid + "-infochart",
  chartType: "line",
  data: {
    index: [props.index?.id ?? -1],
    simulation: [-1],
    simulationRound: [0, maxRoundRange.value]
  },
  chartSettings: {
    scaleColorsVisible: false,
    roundEffectsVisible: false,
    titleVisible: false,
    groupLabelsVisible: false
  },
  itemConfigs: {
    index: {
      [props.index?.id ?? -1]: {
        color: "var(--color-text)",
        open: false
      }
    }
  }
}));

// =====================
// RELATIONS

const outgoingRelations = computed((): RelationItem[] => {
  if (props.index == null) return [];
  return useRelationStore().byStartUid[props.index.uid] ?? [];
});

// =====================
// STAKEHOLDERS

const stakeholders = computed(() => {
  if (props.index == null) return [];
  return useStakeholderStore().byIndex[props.index.id] ?? [];
});

// =====================
// IMAGE

const image = computed(() => {
  if (sceneNode.value && sceneNode.value.data.imageId != null) {
    return imageStore.items[sceneNode.value.data.imageId];
  }
  return null;
});

const imageData = computed(() => {
  if (!image.value) return undefined;
  return imageStore.mediaData[image.value.id] ?? undefined;
});

// =====================
// SECTIONS

function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.index = sections;
}

function loadSections() {
  const savedSections = infoPanelStore.sections?.index || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as keyof typeof sections] = savedSections[key];
    }
  }
}

loadSections();

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}

function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.index = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.index || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as keyof typeof extraSettings] = {
        ...(savedExtraSettings[key] as any)
      };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
</script>

<style></style>
